import { FormEvent, useEffect, useState } from 'react';
import { QuotesApi, RecipesApi } from '../api/resources';
import { ApiError } from '../api/client';
import type { Quote, QuoteFormat, Recipe } from '../api/types';
import { useToast } from '../context/ToastContext';
import { QuoteDocument, quoteShareText } from '../components/QuoteDocument';
import { formatCOP, formatDateTime, formatQty } from '../utils/format';

interface CartItem {
  recipe: Recipe;
  quantity: number;
  unitCost: string;
  totalCost: string;
}

const emptyClient = { clientName: '', clientDocument: '', clientAddress: '', clientPhone: '', clientEmail: '', notes: '' };

function whatsappNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10 ? `57${digits}` : digits;
}

export function Quotes() {
  const { notify } = useToast();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [items, setItems] = useState<Quote[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [client, setClient] = useState(emptyClient);
  const [recipeId, setRecipeId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const [format, setFormat] = useState<QuoteFormat | null>(null);
  const [generated, setGenerated] = useState<Quote | null>(null);

  useEffect(() => {
    RecipesApi.list({ isActive: true }).then((r) => setRecipes(r.recipes));
    QuotesApi.getFormat().then((r) => setFormat(r.format)).catch(() => undefined);
  }, []);

  const cartTotal = cart.reduce((sum, item) => sum + Number(item.totalCost), 0);

  async function load() {
    setLoading(true);
    try {
      const result = await QuotesApi.list({ search: search || undefined, page, pageSize: 15 });
      setItems(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar las cotizaciones.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  async function handleAddItem() {
    const recipe = recipes.find((r) => r.id === recipeId);
    const qty = Number(quantity);
    if (!recipe || !(qty > 0)) {
      notify('Selecciona un articulo y una cantidad mayor a cero.', 'error');
      return;
    }
    const existing = cart.find((item) => item.recipe.id === recipe.id);
    const newQuantity = (existing?.quantity ?? 0) + qty;
    setAdding(true);
    try {
      const { cost, totalProductionCost } = await RecipesApi.cost(recipe.id, newQuantity);
      const line: CartItem = { recipe, quantity: newQuantity, unitCost: String(cost.productionCost), totalCost: totalProductionCost };
      setCart((current) => (existing ? current.map((i) => (i.recipe.id === recipe.id ? line : i)) : [...current, line]));
      setRecipeId('');
      setQuantity('1');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible calcular el costo del articulo.', 'error');
    } finally {
      setAdding(false);
    }
  }

  function clearForm() {
    setClient(emptyClient);
    setCart([]);
    setRecipeId('');
    setQuantity('1');
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!client.clientName.trim()) {
      notify('El nombre del cliente es obligatorio.', 'error');
      return;
    }
    if (!cart.length) {
      notify('Agrega al menos un articulo a la cotizacion.', 'error');
      return;
    }
    setSaving(true);
    try {
      const { quote } = await QuotesApi.create({
        clientName: client.clientName.trim(),
        clientDocument: client.clientDocument.trim() || undefined,
        clientAddress: client.clientAddress.trim() || undefined,
        clientPhone: client.clientPhone.trim() || undefined,
        clientEmail: client.clientEmail.trim() || undefined,
        notes: client.notes.trim() || undefined,
        items: cart.map((i) => ({ recipeId: i.recipe.id, quantity: i.quantity })),
      });
      notify(`Cotizacion ${quote.code} generada correctamente.`, 'success');
      clearForm();
      setGenerated(quote);
      setPage(1);
      load();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible generar la cotizacion.', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function openQuote(id: string) {
    try {
      const { quote } = await QuotesApi.get(id);
      setGenerated(quote);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible abrir la cotizacion.', 'error');
    }
  }

  function sendByEmail(quote: Quote) {
    if (!format) return;
    const subject = encodeURIComponent(`Cotización ${quote.code} - ${format.companyName}`);
    const body = encodeURIComponent(quoteShareText(quote, format));
    window.location.href = `mailto:${quote.clientEmail ?? ''}?subject=${subject}&body=${body}`;
  }

  function sendByWhatsapp(quote: Quote) {
    if (!format) return;
    const number = quote.clientPhone ? whatsappNumber(quote.clientPhone) : '';
    const text = encodeURIComponent(quoteShareText(quote, format));
    window.open(`https://wa.me/${number}?text=${text}`, '_blank', 'noopener');
  }

  return (
    <div>
      <div className="flex-between">
        <h1>Cotizaciones</h1>
      </div>

      <div className="card">
        <h2>Nueva cotizacion</h2>
        <form onSubmit={handleSubmit} noValidate>
          <h3>Datos del cliente</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="qdoc">Número de documento</label>
              <input id="qdoc" value={client.clientDocument} onChange={(e) => setClient({ ...client, clientDocument: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="qclient">Nombre</label>
              <input id="qclient" required value={client.clientName} onChange={(e) => setClient({ ...client, clientName: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="qaddr">Dirección</label>
              <input id="qaddr" value={client.clientAddress} onChange={(e) => setClient({ ...client, clientAddress: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="qphone">Teléfono</label>
              <input id="qphone" type="tel" value={client.clientPhone} onChange={(e) => setClient({ ...client, clientPhone: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="qemail">Correo (para enviar la cotización)</label>
              <input id="qemail" type="email" value={client.clientEmail} onChange={(e) => setClient({ ...client, clientEmail: e.target.value })} />
            </div>
          </div>

          <h3>Artículos</h3>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="qrecipe">Arreglo</label>
              <select id="qrecipe" value={recipeId} onChange={(e) => setRecipeId(e.target.value)}>
                <option value="">Selecciona...</option>
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} - {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="qqty">Cantidad</label>
              <input id="qqty" type="number" min={1} step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
          </div>
          <button type="button" className="btn btn-secondary" onClick={handleAddItem} disabled={adding || !recipeId}>
            {adding ? 'Agregando...' : 'Agregar artículo'}
          </button>

          {cart.length > 0 && (
            <div className="table-wrap mt-1">
              <table>
                <thead>
                  <tr>
                    <th>Artículo</th>
                    <th>Descripción</th>
                    <th className="text-right">Cantidad</th>
                    <th className="text-right">Valor unitario</th>
                    <th className="text-right">Valor total</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item) => (
                    <tr key={item.recipe.id}>
                      <td>
                        <strong>{item.recipe.name}</strong>
                        <br />
                        <small className="text-muted">{item.recipe.code}</small>
                      </td>
                      <td>{item.recipe.description || '—'}</td>
                      <td className="text-right">{formatQty(item.quantity)}</td>
                      <td className="text-right">{formatCOP(item.unitCost)}</td>
                      <td className="text-right">{formatCOP(item.totalCost)}</td>
                      <td>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => setCart(cart.filter((i) => i.recipe.id !== item.recipe.id))}>
                          Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 700 }}>
                    <td colSpan={4} className="text-right">Total de la cotización</td>
                    <td className="text-right">{formatCOP(cartTotal)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div className="field mt-1">
            <label htmlFor="qnotes">Observaciones</label>
            <textarea id="qnotes" value={client.notes} onChange={(e) => setClient({ ...client, notes: e.target.value })} />
          </div>

          <div className="actions-row">
            <button type="submit" className="btn btn-primary" disabled={saving || !cart.length}>
              {saving ? 'Generando...' : 'Generar cotizacion'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={clearForm} disabled={saving}>
              Limpiar
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Cotizaciones registradas</h2>
          <input type="search" placeholder="Buscar cliente, codigo..." value={search} onChange={(e) => { setPage(1); setSearch(e.target.value); }} style={{ maxWidth: 260 }} />
        </div>
        {loading ? (
          <div className="loading-block">Cargando cotizaciones...</div>
        ) : items.length === 0 ? (
          <p className="empty-state">No hay cotizaciones registradas.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Codigo</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Artículos</th>
                  <th className="text-right">Total</th>
                  <th>Usuario</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((q) => (
                  <tr key={q.id}>
                    <td>{q.code}</td>
                    <td>{q.clientName}</td>
                    <td>{formatDateTime(q.date)}</td>
                    <td>{q.items?.length ? q.items.map((i) => `${i.nameSnapshot} x ${formatQty(i.quantity)}`).join(', ') : q.recipeNameSnapshot}</td>
                    <td className="text-right">{formatCOP(q.totalCost)}</td>
                    <td>{q.user?.fullName}</td>
                    <td>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => openQuote(q.id)}>
                        Ver / Imprimir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination">
          <span>
            {total} resultado(s) - pagina {page} de {totalPages}
          </span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </button>
          <button type="button" className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </button>
        </div>
      </div>

      {generated && format && (
        <div
          className="platform-modal-backdrop quote-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setGenerated(null);
          }}
        >
          <section className="platform-modal quote-modal" role="dialog" aria-modal="true" aria-label="Cotizacion generada">
            <div className="actions-row quote-modal-actions">
              <button type="button" className="btn btn-primary" onClick={() => window.print()}>
                Imprimir
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => sendByEmail(generated)}>
                Enviar por correo
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => sendByWhatsapp(generated)}>
                Enviar por WhatsApp
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setGenerated(null)}>
                Cerrar
              </button>
            </div>
            <QuoteDocument quote={generated} format={format} />
          </section>
        </div>
      )}
    </div>
  );
}
